/**
 * Stage 33/37 — pure presentation rules for the broad cross-unit surface.
 *
 * Everything that decides *what text a user sees* lives outside React and outside the
 * network, so the semantic guarantees are unit-testable rather than merely reviewed.
 *
 * WHAT THIS MODULE IS NOW
 * Stage 37 moved the analyst-facing wording of the scenarios 6–7 case card to
 * `cross-unit.case-view.ts`. What remains here is the small set of catalog-level helpers
 * that card still builds on: the source-day date format and the «Открыть полный кейс» target.
 *
 * WHAT WAS DELETED WITH THE OLD CARD (Stage 37)
 * `renderTrackA` / `renderAnomaly` / `renderPairingStrength`, the pairing-status and
 * membership dictionaries, `buildPairBadges`, `buildPairWarnings` / `visibleDisclaimers`
 * and the four disclaimer constants, `renderLegField` / `renderSummaryField` /
 * `renderLegUnit` / `renderLegRoute`, `formatTons`, the decile helper and the score-label
 * dictionaries. They rendered the candidate catalog — three score tiles, ranks, per-field
 * null causes and four repeated model caveats — which is exactly the surface the analyst
 * card replaced. They are removed rather than left dead: an unused label is still a label
 * the next reader will wire back up, and their sentences must not survive in the bundle.
 *
 * Score values, levels, SHAP contributions and reason codes are read, never recomputed —
 * that rule is unchanged.
 */

/* ── null semantics ───────────────────────────────────────────────────────── */

import type { TranslateFn } from '@/lib/i18n'

/**
 * The three-way null vocabulary (`field_not_in_source` / `source_value_missing` /
 * `leg_unavailable`) went with the leg-field renderers: the analyst card hides an absent
 * value instead of naming the cause, and the per-field statuses are listed verbatim in
 * «Технические сведения». What is left is the one label used when a value that IS
 * expected cannot be formatted.
 */

export const valueMissing = (t: TranslateFn): string => t('common.state.empty')

/** Dates are shown as the source day; an unparsable date is never invented. */
export function formatDay(v: unknown, t: TranslateFn): string {
  const s = String(v ?? '').trim()
  if (!s) return valueMissing(t)
  const iso = s.slice(0, 10)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return s
  return `${m[3]}.${m[2]}.${m[1]}`
}

/* ── Stage 38: the SHAP normalisation moved out with the card block ──────────
 *
 * `renderShap` / `renderShapFactor` turned the ML priority model's SHAP factors into
 * signed, Russian-labelled rows for «Что повлияло на приоритет проверки». That block is
 * gone: SHAP explains the priority model, while the card explains what the anomaly model
 * flagged, and the two were being read as one thing.
 *
 * The SHAP payload is untouched and still shown — as raw `feature=contribution` pairs plus
 * the base value and model version inside «Технические сведения», rendered straight from
 * `explanations.trackAShap` with no normalisation layer in between.
 */

/* ── route helper ─────────────────────────────────────────────────────────── */

/**
 * «Открыть полный кейс» target. Scenarios 6–7 address the case page by
 * `canonical_pair_id`; a synthetic numeric case_id is never fabricated.
 */
export function buildFullCaseHref(canonicalPairId: string, scenarioId: number): string {
  return `/cases/${encodeURIComponent(canonicalPairId)}?scenario_id=${scenarioId}`
}
